import {
  Badge,
  confirm,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { ShippingCarrierDTO } from "../api/client";

function nameLo(row: ShippingCarrierDTO): string {
  return (row.name as { lo?: string })?.lo ?? "—";
}

type CarriersTableProps = {
  canEdit: boolean;
  canDelete: boolean;
  isLoading: boolean;
  data: ShippingCarrierDTO[];
  onEdit: (row: ShippingCarrierDTO) => void;
  onDelete: (id: string) => void | Promise<void>;
};

export function CarriersTable({
  canEdit,
  canDelete,
  isLoading,
  data,
  onEdit,
  onDelete,
}: CarriersTableProps) {
  const columns: TanstackReactTable.ColumnDef<ShippingCarrierDTO>[] = [
    {
      id: "code",
      header: "ລະຫັດ",
      cell: ({ row }) => <span className="font-mono">{row.original.code}</span>,
      size: 100,
    },
    {
      id: "nameLo",
      header: "ຊື່ (ລາວ)",
      cell: ({ row }) => nameLo(row.original),
      size: 140,
    },
    {
      id: "enabled",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={row.original.enabled ? "success" : "secondary"}>
          {row.original.enabled ? "ເປີດ" : "ປິດ"}
        </Badge>
      ),
      size: 90,
    },
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      cell: ({ row }) => row.original.sortOrder,
      size: 72,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const actions = [];
        if (canEdit) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }
        if (canDelete) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຜູ້ຂົນສົ່ງ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ «${row.original.code}»? ອໍເດີ້ທີ່ອ້າງອີງຈະຖືກຕັດຄວາມສໍາພັນ (SET NULL).`,
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) await onDelete(row.original.id);
            },
          });
        }
        if (actions.length === 0) return null;
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 120,
    },
  ];

  const totalCount = data.length;
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
  const limit =
    totalCount === 0
      ? 20
      : (PAGE_SIZE_OPTIONS.find((n) => n >= totalCount) ?? 100);

  return (
    <DataTable<ShippingCarrierDTO, unknown>
      noDataMessage="ບໍ່ມີຜູ້ຂົນສົ່ງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={0}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={() => {}}
    />
  );
}
